import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice } from '../utils/formatHelper';
import kitoLogo from '../assets/images/kito-logo.avif';
import { userNotificationsApi, handleNotificationError } from '../utils/notificationsApi';

function Header() {
  const { totalItems, totalPrice } = useSubscription();
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navbarCollapseRef = useRef(null);
  const navbarTogglerRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  // Close mobile menu when clicking on a menu item
  const handleNavLinkClick = () => {
    if (window.innerWidth < 992) {
      closeNavbar();
    }
  };

  // Close mobile menu
  const closeNavbar = () => {
    if (navbarCollapseRef.current && navbarCollapseRef.current.classList.contains('show')) {
      if (navbarTogglerRef.current) {
        navbarTogglerRef.current.click();
      } else {
        // Fallback if ref is not available
        const bsCollapse = new window.bootstrap.Collapse(navbarCollapseRef.current);
        bsCollapse.hide();
      }
    }
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        navbarCollapseRef.current &&
        navbarCollapseRef.current.classList.contains('show') &&
        !navbarCollapseRef.current.contains(event.target) &&
        !navbarTogglerRef.current.contains(event.target)
      ) {
        closeNavbar();
      }
    };

    // Close mobile menu when route changes
    const handleRouteChange = () => {
      closeNavbar();
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  // Notifications
  const fetchUnreadCount = async () => {
    try {
      const res = await userNotificationsApi.unreadCount();
      if (res?.success) {
        setUnreadCount(res.count || res.unread || 0);
      }
    } catch (error) {
      handleNotificationError(error, 'Bildirim sayısı alınamadı');
    }
  };

  const fetchRecentNotifications = async () => {
    try {
      setNotifLoading(true);
      const res = await userNotificationsApi.list(1, 5);
      if (res?.success) {
        setNotifications(res.data || res.notifications || []);
        setUnreadCount(res.unreadCount || res.countUnread || unreadCount);
      }
    } catch (error) {
      handleNotificationError(error, 'Bildirimler alınamadı');
    } finally {
      setNotifLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      // Bildirimleri sadece dropdown açıldığında yükle, otomatik yükleme
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const renderMobileNotificationBell = () => {
    // Sepet gibi header’da her zaman görünür aksiyon: bildirim zili (sadece mobilde)
    // Sadece giriş yapmış kullanıcılar için gösteriyoruz.
    if (!isAuthenticated) return null;

    return (
      <div className="dropdown d-lg-none me-2">
        <a
          className="mobile-notification-btn position-relative dropdown-toggle-no-caret"
          href="#"
          role="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          onClick={fetchRecentNotifications}
        >
          <i className="fas fa-bell"></i>
          {unreadCount > 0 && (
            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </a>

        <ul
          className="dropdown-menu dropdown-menu-end shadow"
          style={{ minWidth: '320px', padding: 0, marginTop: '10px', borderRadius: '12px', overflow: 'hidden' }}
        >
          <li className="d-flex justify-content-between align-items-center p-3 border-bottom bg-light">
            <span className="fw-bold text-dark">Bildirimler</span>
            <Link to="/notifications" className="small text-decoration-none fw-semibold text-dark" onClick={handleNavLinkClick}>
              Tümünü Gör
            </Link>
          </li>

          <li style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {notifLoading ? (
              <div className="py-4 text-center">
                <div className="spinner-border spinner-border-sm text-orange" role="status"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-5 text-center text-muted">
                <i className="fas fa-bell-slash fa-2x mb-2 opacity-50" style={{ color: '#000' }}></i>
                <p className="small mb-0">Henüz bildiriminiz yok</p>
              </div>
            ) : (
              notifications.slice(0, 5).map((n) => (
                <div
                  key={n.id}
                  className={`dropdown-item border-bottom ${!n.isRead ? 'bg-orange-light' : ''}`}
                  style={{ padding: '0.75rem 1rem', whiteSpace: 'normal' }}
                >
                  <div className="d-flex w-100 justify-content-between mb-1">
                    <strong className="small text-truncate" style={{ maxWidth: '200px' }}>{n.title || 'Bildirim'}</strong>
                    {!n.isRead && <span className="badge bg-warning text-dark" style={{ fontSize: '0.6rem' }}>YENİ</span>}
                  </div>
                  <p className="mb-0 small text-muted text-break" style={{ lineHeight: '1.4' }}>{n.message}</p>
                </div>
              ))
            )}
          </li>

          {notifications.length > 0 && (
            <li className="p-2 border-top bg-light text-center">
              <button
                className="btn btn-sm text-decoration-none text-dark small"
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    await userNotificationsApi.markAllRead();
                    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
                    setUnreadCount(0);
                  } catch (error) {
                    handleNotificationError(error, 'Bildirimler okundu işaretlenemedi');
                  }
                }}
              >
                <i className="fas fa-check-double me-1"></i> Tümünü Okundu İşaretle
              </button>
            </li>
          )}
        </ul>
      </div>
    );
  };

  const renderAuthButtons = () => {
    if (isAuthenticated) {
      // Tüm roller için normal header
      return (
        <>
          {user?.role === 'SUPER_ADMIN' || user?.role === 'COMPANY_ADMIN' ? (
            <>
              <li className="nav-item">
                <Link
                  to="/admin"
                  className="nav-link"
                  onClick={handleNavLinkClick}
                >
                  <i className="fas fa-cog me-2"></i>
                  Admin Panel
                </Link>
              </li>
            </>
          ) : null}

          {/* Bildirim Zili - Standalone (sadece desktop) */}
          <li className="nav-item dropdown me-2 d-none d-lg-block">
            <a
              className="nav-link dropdown-toggle position-relative"
              href="#"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              onClick={fetchRecentNotifications}
            >
              <i className="fas fa-bell" style={{ fontSize: '1.2rem', color: '#000' }}></i>
              {unreadCount > 0 && (
                <span
                  className="position-absolute top-10 start-90 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: '0.6rem', padding: '0.25em 0.5em', marginTop: '-5px' }}
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </a>
            <ul
              className="dropdown-menu dropdown-menu-end shadow"
              style={{ minWidth: '320px', padding: 0, marginTop: '10px', borderRadius: '12px', overflow: 'hidden' }}
            >
              <li className="d-flex justify-content-between align-items-center p-3 border-bottom bg-light">
                <span className="fw-bold text-dark">Bildirimler</span>
                <Link to="/notifications" className="small text-decoration-none fw-semibold text-dark" onClick={handleNavLinkClick}>
                  Tümünü Gör
                </Link>
              </li>

              <li style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {notifLoading ? (
                  <div className="py-4 text-center">
                    <div className="spinner-border spinner-border-sm text-orange" role="status"></div>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="py-5 text-center text-muted">
                    <i className="fas fa-bell-slash fa-2x mb-2 opacity-50" style={{ color: '#000' }}></i>
                    <p className="small mb-0">Henüz bildiriminiz yok</p>
                  </div>
                ) : (
                  notifications.slice(0, 5).map((n) => (
                    <div
                      key={n.id}
                      className={`dropdown-item border-bottom ${!n.isRead ? 'bg-orange-light' : ''}`}
                      style={{ padding: '0.75rem 1rem', whiteSpace: 'normal' }}
                    >
                      <div className="d-flex w-100 justify-content-between mb-1">
                        <strong className="small text-truncate" style={{ maxWidth: '200px' }}>
                          {n.title || 'Bildirim'}
                        </strong>
                        {!n.isRead && (
                          <span className="badge bg-warning text-dark" style={{ fontSize: '0.6rem' }}>
                            YENİ
                          </span>
                        )}
                      </div>
                      <p className="mb-0 small text-muted text-break" style={{ lineHeight: '1.4' }}>
                        {n.message}
                      </p>
                    </div>
                  ))
                )}
              </li>

              {notifications.length > 0 && (
                <li className="p-2 border-top bg-light text-center">
                  <button
                    className="btn btn-sm text-decoration-none text-dark small"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        await userNotificationsApi.markAllRead();
                        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
                        setUnreadCount(0);
                      } catch (error) {
                        handleNotificationError(error, 'Bildirimler okundu işaretlenemedi');
                      }
                    }}
                  >
                    <i className="fas fa-check-double me-1"></i> Tümünü Okundu İşaretle
                  </button>
                </li>
              )}
            </ul>
          </li>

          {/* Profil Menüsü - Temizlendi */}
          <li className="nav-item dropdown">
            <a
              className={`nav-link dropdown-toggle ${isActive('/admin/profile') || isActive('/dashboard') ? 'active-nav-link' : ''}`}
              href="#"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="fas fa-user me-2"></i>
              {user?.fullName || 'Hesabım'}
            </a>
            <ul className="dropdown-menu dropdown-menu-end shadow-sm" style={{ minWidth: '220px', borderRadius: '8px' }}>
              <div className="px-3 py-2 border-bottom bg-light">
                <div className="fw-bold small text-dark">{user?.fullName}</div>
                <div className="small text-muted">{user?.email}</div>
              </div>

              {((user?.role === 'SUPER_ADMIN' || user?.role === 'COMPANY_ADMIN')) && (
                <>
                  <li>
                    <Link to="/admin/profile" className="dropdown-item py-2" onClick={handleNavLinkClick}>
                      <i className="fas fa-user-cog me-2" style={{ color: '#000' }}></i> Profil Ayarları
                    </Link>
                  </li>
                  <li>
                    <Link to="/dashboard" className="dropdown-item py-2" onClick={handleNavLinkClick}>
                      <i className="fas fa-columns me-2" style={{ color: '#000' }}></i> Kullanıcı Paneli
                    </Link>
                  </li>
                </>
              )}

              {(!(user?.role === 'SUPER_ADMIN' || user?.role === 'COMPANY_ADMIN')) && (
                <>
                  <li>
                    <Link to="/dashboard" className="dropdown-item py-2" onClick={handleNavLinkClick}>
                      <i className="fas fa-cog me-2" style={{ color: '#000' }}></i> Hesap Ayarları
                    </Link>
                  </li>

                </>
              )}

              <li><hr className="dropdown-divider" /></li>
              <li>
                <button
                  className="dropdown-item text-danger py-2"
                  onClick={(e) => {
                    handleNavLinkClick();
                    logout();
                  }}
                >
                  <i className="fas fa-sign-out-alt me-2"></i> Çıkış Yap
                </button>
              </li>
            </ul>
          </li>
        </>
      );
    }

    return (
      <>
        <li className="nav-item">
          <Link
            to="/login"
            className="nav-link"
            onClick={handleNavLinkClick}
          >
            <i className="fas fa-sign-in-alt me-2"></i>
            Giriş Yap
          </Link>
        </li>
        <li className="nav-item">
          <Link
            to="/signup"
            className="nav-link"
            onClick={handleNavLinkClick}
          >
            <i className="fas fa-user-plus me-2"></i>
            Üye Ol
          </Link>
        </li>
      </>
    );
  };

  return (
    <header className="fixed-top" style={{ zIndex: 1030 }}>
      <nav className="navbar navbar-expand-lg bg-white shadow-sm py-2">
        <div className="container">
          <div className="d-flex align-items-center">
            <Link className="navbar-brand" to="/" onClick={handleNavLinkClick}>
              <img
                src={kitoLogo}
                alt="Kito"
                style={{ height: '32px', display: 'block' }}
              />
            </Link>
            {/* Destek menüsü logonun hemen sağında */}
            <div className="support-menu-item dropdown ms-2" style={{ marginTop: 0, marginBottom: 0, display: 'flex', alignItems: 'center' }}>
              <a
                className="nav-link dropdown-toggle"
                href="#"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{ color: '#E86C3A', fontWeight: 600, display: 'flex', alignItems: 'center', paddingTop: 0, paddingBottom: 0 }}
              >
                <i className="fas fa-headset me-2"></i>
                <span className="support-text">Destek</span>
              </a>
              <ul className="dropdown-menu">
                <li>
                  <a href="tel:08504500535" className="dropdown-item">
                    <i className="fas fa-phone-alt me-2"></i>
                    0850 450 05 35
                  </a>
                </li>
                <li>
                  <a href="tel:02169060535" className="dropdown-item">
                    <i className="fas fa-phone-alt me-2"></i>
                    0216 906 05 35
                  </a>
                </li>
                <li>
                  <a href="mailto:info@kito.pet" className="dropdown-item">
                    <i className="fas fa-envelope me-2"></i>
                    info@kito.pet
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="d-flex align-items-center">
            {/* Mobil Paketim Butonu */}
            {(!isAuthenticated || user?.role !== 'SHIPPING_STAFF') && (
              <Link
                to="/subscription/package"
                className="mobile-package-btn d-lg-none position-relative me-2"
                onClick={handleNavLinkClick}
              >
                <i className="fas fa-shopping-basket"></i>
                {totalItems > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {totalItems}
                  </span>
                )}
              </Link>
            )}

            {renderMobileNotificationBell()}

            <button
              ref={navbarTogglerRef}
              className="navbar-toggler border-0"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarNav"
              aria-controls="navbarNav"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <i className="fas fa-bars text-olive"></i>
            </button>
          </div>

          <div ref={navbarCollapseRef} className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto align-items-lg-center">
              {/* Kargo personeli değilse bu linkleri göster */}
              {(!isAuthenticated || user?.role !== 'SHIPPING_STAFF') && (
                <>
                  {/* Desktop Paketim Butonu */}
                  <li className="nav-item package-item d-none d-lg-block">
                    <Link
                      to="/subscription/package"
                      className={`btn position-relative px-4 py-2 ${totalItems > 0 || location.pathname.match(/^\/subscription\/(package|payment|complete)/) ? 'btn-orange' : 'btn-outline-orange'
                        }`}
                      onClick={handleNavLinkClick}
                    >
                      <div className="d-flex align-items-center justify-content-center">
                        <i className="fas fa-shopping-basket me-2"></i>
                        <div>
                          <div className="fw-bold">Paketim</div>
                          <div style={{ fontSize: '0.8rem' }}>
                            {totalItems > 0 ? (
                              <span>{totalItems} Ürün - {formatPrice(totalPrice)}</span>
                            ) : (
                              <span>Paket Boş</span>
                            )}
                          </div>
                        </div>
                        {totalItems > 0 && (
                          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                            style={{ marginTop: '5px' }}>
                            {totalItems}
                          </span>
                        )}
                      </div>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      className={`nav-link ${isActive('/products') ? 'active-nav-link' : ''}`}
                      to="/products"
                      onClick={handleNavLinkClick}
                    >
                      <i className="fas fa-box me-2"></i>
                      Ürünler
                    </Link>
                  </li>
                  {isAuthenticated && (
                    <li className="nav-item">
                      <Link
                        className={`nav-link ${isActive('/subscription/user') ? 'active-nav-link' : ''}`}
                        to={`/subscription/user/${user?.id}`}
                        onClick={handleNavLinkClick}
                      >
                        <i className="fas fa-sync me-2"></i>
                        Aboneliklerim
                      </Link>
                    </li>
                  )}
                  {isAuthenticated && !(user?.role === 'SUPER_ADMIN' || user?.role === 'COMPANY_ADMIN') && (
                    <li className="nav-item">
                      <Link
                        className={`nav-link ${isActive('/orders') ? 'active-nav-link' : ''}`}
                        to="/orders"
                        onClick={handleNavLinkClick}
                      >
                        <i className="fas fa-box me-2"></i>
                        Siparişlerim
                      </Link>
                    </li>
                  )}
                </>
              )}
              {renderAuthButtons()}
            </ul>
          </div>
        </div>
      </nav>

      <style>
        {`
          .navbar {
            min-height: 64px;
          }
          .active-nav-link {
            color: var(--kito-orange) !important;
            font-weight: 600;
          }
          .nav-link {
            position: relative;
            cursor: pointer;
          }
          .active-nav-link::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 2px;
            background-color: var(--kito-orange);
          }
          .btn-orange, .btn-outline-orange {
            transition: all 0.3s ease;
          }
          .btn-orange:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(232, 108, 58, 0.2);
          }
          .btn-outline-orange:hover {
            transform: translateY(-2px);
            background-color: var(--kito-orange);
            color: white;
            box-shadow: 0 4px 12px rgba(232, 108, 58, 0.2);
          }
          .dropdown-menu {
            border: none;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            border-radius: 8px;
            padding: 0.5rem;
          }
          
          .dropdown-item {
            padding: 0.5rem 1rem;
            border-radius: 4px;
            transition: all 0.2s ease;
          }
          
          .dropdown-item:hover {
            background-color: rgba(var(--kito-orange-rgb), 0.1);
            color: var(--kito-orange);
          }
          
          .dropdown-item.active {
            background-color: var(--kito-orange);
            color: white;
          }
          
          .dropdown-item.active:hover {
            background-color: var(--kito-orange);
            color: white;
          }
          
          .dropdown-item i {
            width: 20px;
            text-align: center;
          }

          .navbar-toggler:focus {
            box-shadow: none;
          }

          .navbar-collapse {
            background: white;
          }

          .package-item {
            margin: 0 1rem;
          }

          .mobile-package-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 42px;
            height: 42px;
            border-radius: 8px;
            color: var(--kito-orange);
            font-size: 1.2rem;
            transition: all 0.2s ease;
            background-color: rgba(var(--kito-orange-rgb), 0.1);
          }

          .mobile-package-btn:hover {
            background-color: var(--kito-orange);
            color: white;
          }

          .mobile-package-btn .badge {
            font-size: 0.65rem;
            padding: 0.35em 0.5em;
            min-width: 1.5em;
          }

          .dropdown-toggle-no-caret::after {
            display: none !important;
          }

          .mobile-notification-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 42px;
            height: 42px;
            border-radius: 8px;
            color: var(--kito-orange);
            font-size: 1.2rem;
            transition: all 0.2s ease;
            background-color: rgba(var(--kito-orange-rgb), 0.1);
            text-decoration: none;
            border: none;
          }

          .mobile-notification-btn:hover {
            background-color: var(--kito-orange);
            color: white;
          }

          .mobile-notification-btn .badge {
            font-size: 0.65rem;
            padding: 0.35em 0.5em;
            min-width: 1.5em;
          }

          .support-menu-item {
            display: inline-block;
            margin-left: 32px;
            background: rgba(232,108,58,0.12); /* Soft, saydam turuncu */
            border-radius: 8px;
            padding: 0 0.5rem;
            vertical-align: middle;
            align-items: center;
          }
          .support-menu-item .nav-link {
            color: var(--kito-orange, #E86C3A) !important;
            font-weight: 600;
            padding: 0 1rem;
            border-radius: 8px;
            display: flex;
            align-items: center;
            padding-top: 0;
            padding-bottom: 0;
          }
          .support-menu-item .nav-link:hover,
          .support-menu-item .nav-link:focus {
            background: var(--kito-orange, #E86C3A);
            color: #fff !important;
          }
          .support-menu-item .dropdown-menu {
            margin-top: 8px;
          }
          @media (max-width: 991px) {
            .support-menu-item {
              display: inline-block;
              width: auto;
              min-width: 0;
              margin: 0 0 8px 0;
              padding: 0;
              border-radius: 8px;
              background: rgba(232,108,58,0.12);
              align-items: center;
            }
            .support-menu-item .nav-link {
              font-size: 1rem;
              padding: 0.5rem 0.75rem;
              border-radius: 8px;
              gap: 0.3rem;
              padding-top: 0;
              padding-bottom: 0;
              align-items: center;
            }
          }
          @media (max-width: 576px) {
            .support-menu-item {
              max-width: 110px;
              margin-left: 8px;
              padding: 0;
              background: transparent;
            }
            .support-menu-item .nav-link {
              font-size: 1rem;
              padding: 0;
              max-width: none;
            }
            .support-menu-item .support-text {
              display: none;
            }
          }
        `}
      </style>
    </header>
  );
}

export default Header;
